/**
 * Write hooks — the mutation counterpart of the read hooks around them.
 *
 * Reads were always hook-wrapped; writes used to be raw documents called with
 * useMutation inside screens, which meant @apollo/client leaked into the view
 * layer. Every write now comes through here, so transport stays an api/
 * concern and a document change lands in one place.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useLazyQuery, useSubscription } from '@apollo/client';
import {
  RESET_PASSWORD, SEND_FORGOT_PASSWORD_OTP, UPDATE_PASSWORD, VERIFY_FORGOT_PASSWORD_OTP,
} from '../mutations/auth';
import {
  LOGIN_USER, LOGIN_WITH_PHONE, LOGOUT_USER, SIGNUP_USER, VERIFY_PHONE_LOGIN_OTP,
} from '../mutations/auth';
import { UPDATE_PROFILE } from '../mutations/account';
import {
  CREATE_FIRM, REQUEST_FIRM_MEMBERSHIP, APPROVE_FIRM_MEMBER, REJECT_FIRM_MEMBER, ADD_FIRM_CLIENT,
  ADD_FIRM_CLIENT_WITHOUT_SMS,
} from '../mutations/firm';
import {
  CREATE_CLIENT, CREATE_PROSPECT, CREATE_PORTFOLIO, CREATE_PORTFOLIO_FROM_HOLDINGS,
} from '../mutations/onboarding';
import { RECOMPUTE_PORTFOLIO_FULL, DELETE_PORTFOLIOS, BACKTEST_PORTFOLIO_TRADES } from '../mutations/portfolios';
import {
  COMPUTE_BRINSON_ATTRIBUTION, CALCULATE_RETURN_ATTRIBUTION, SETUP_DEFAULT_SECURITY_GROUPS,
  COMPUTE_MULTIFACTOR_ATTRIBUTION,
  COMPUTE_MF_MULTIFACTOR_ATTRIBUTION,
} from '../mutations/attribution';
import { COMPUTE_PORTFOLIO_RISK, COMPUTE_PORTFOLIO_EXPOSURES } from '../mutations/risk';
import {
  RUN_ALPHA_BACKTEST, CALIBRATE_ALPHA_WEIGHTS, REGENERATE_ALPHA_SIGNALS,
} from '../mutations/alpha';
import {
  REVIEW_ALPHA_THESIS, PUBLISH_ALPHA_THESIS, ACKNOWLEDGE_ALPHA_ALERT, RESOLVE_ALPHA_ALERT,
  HALT_ALPHA_PAPER_BOOK, RESUME_ALPHA_PAPER_BOOK, GENERATE_COMPANY_REPORT, RUN_ALPHA_SCORING,
} from '../mutations/stockAlpha';
import {
  TRIGGER_STOCK_RESEARCH_AGENT, VERIFY_STOCK_REPORT,
} from '../mutations/stocks';
import {
  RUN_MF_PM_REVIEW, RUN_FUND_MANAGER_REVIEW, RUN_ADVISOR_BOOK_REVIEW,
  RUN_MODEL_REVIEW, RENDER_PORTFOLIO_REVIEW, RUN_MUTUAL_FUND_REVIEW, RUN_PORTFOLIO_REVIEW,
  TRIGGER_MF_RESEARCH_BATCH_AGENT,
} from '../mutations/reviews';
import { SAVE_INVESTMENT_PROPOSAL } from '../mutations/proposals';
import { RUN_PORTFOLIO_ANALYTICS } from '../mutations/jobs';
import { useBackgroundJobs } from '../backgroundJobs';
import { asClient } from '../clientScope';
import { ANALYSIS_KIND, JOB_TYPES } from '../../config/jobTypes';
import { REVIEW_RUN, isReviewRunActive, newestReviewRun, reviewRunStep } from '../../lib/reviewRun';
/* Stand-ins for a goal type with no finalize or no planner — the hooks skip,
   but useMutation and useSubscription still need a document. */
import { FINALIZE_CAPITAL_TARGET_PLAN } from '../mutations/goals';
import { PLAN_CAPITAL_TARGET } from '../subscriptions/goals';
import { ANALYZE_SCENARIO, RETURN_ATTRIBUTION, TRIGGERED_EVENTS } from '../queries';

export const useLogin = () => useMutation(LOGIN_USER);
/* Phone + OTP, BoloDhan's own way in: request the code, then exchange it. */
export const useLoginWithPhone = () => useMutation(LOGIN_WITH_PHONE);
export const useVerifyPhoneOtp = () => useMutation(VERIFY_PHONE_LOGIN_OTP);
export const useSignup = () => useMutation(SIGNUP_USER);
export const useLogoutUser = () => useMutation(LOGOUT_USER);
/* The account's own details. Refetches the profile so the name, email and phone
   shown around the app follow the change without a reload. */
export const useUpdateProfile = () => useMutation(UPDATE_PROFILE, { refetchQueries: ['GetProfile'] });

/* Firm membership: a founder creates the firm; anyone else requests to join and
   the owner approves. Both refetch the membership so every screen sees it. */
const MEMBERSHIP_REFETCH = { refetchQueries: ['MyFirmMembership'], awaitRefetchQueries: true };
export const useCreateFirm = () => useMutation(CREATE_FIRM, MEMBERSHIP_REFETCH);
export const useRequestFirmMembership = () => useMutation(REQUEST_FIRM_MEMBERSHIP, MEMBERSHIP_REFETCH);
export const useApproveFirmMember = () => useMutation(APPROVE_FIRM_MEMBER, { refetchQueries: ['FirmMembers'] });
export const useRejectFirmMember = () => useMutation(REJECT_FIRM_MEMBER, { refetchQueries: ['FirmMembers'] });
/* A client joins the firm's book by consent — a request, or an invite. */
const CLIENT_REFETCH = { refetchQueries: ['FirmClients'] };
/** A backend that does not know `smsSent` yet — see ADD_FIRM_CLIENT_WITHOUT_SMS. */
const UNKNOWN_SMS_FIELD = /cannot query field '?smsSent'?/i;

/**
 * Add a client, on a backend with or without the invite-SMS field.
 *
 * GraphQL validates the whole document before executing any of it, so asking
 * for a field the server has not deployed does not degrade — it refuses the
 * write. The first refusal of that exact kind falls back to the document
 * without it, so adding a client keeps working while the backend catches up,
 * and starts reporting the SMS the moment it does.
 */
export const useAddFirmClient = () => {
  const [withSms, state] = useMutation(ADD_FIRM_CLIENT, CLIENT_REFETCH);
  const [withoutSms] = useMutation(ADD_FIRM_CLIENT_WITHOUT_SMS, CLIENT_REFETCH);
  const [fallbackBusy, setFallbackBusy] = useState(false);

  const add = useCallback(async (options) => {
    try {
      return await withSms(options);
    } catch (err) {
      if (!UNKNOWN_SMS_FIELD.test(String(err?.message ?? ''))) throw err;
      setFallbackBusy(true);
      try {
        return await withoutSms(options);
      } finally {
        setFallbackBusy(false);
      }
    }
  }, [withSms, withoutSms]);

  return [add, { ...state, loading: state.loading || fallbackBusy }];
};

export const useCreateClient = () => useMutation(CREATE_CLIENT);
export const useCreateProspect = () => useMutation(CREATE_PROSPECT);
export const useCreatePortfolio = () => useMutation(CREATE_PORTFOLIO);

/* Keeping a recommendation as a proposal. Send the client through `asClient`
   at call time: the plan must belong to the user it is saved for. */
export const useSaveInvestmentProposal = () => useMutation(SAVE_INVESTMENT_PROPOSAL);

export const useCreatePortfolioFromHoldings = () => useMutation(CREATE_PORTFOLIO_FROM_HOLDINGS);

export const useComputeAttribution = () => useMutation(COMPUTE_BRINSON_ATTRIBUTION);

/**
 * The return-attribution runner's three calls.
 *
 * Generating and loading send identical variables and answer with the same
 * payload; the difference is that the mutation always recomputes, while the
 * query returns what is stored and computes only when the window has nothing.
 * Neither is cached — a run is asked for precisely when the stored answer is in
 * doubt, and a cached one would defeat the button.
 */
export const useCalculateAttribution = () =>
  useMutation(CALCULATE_RETURN_ATTRIBUTION, { fetchPolicy: 'no-cache' });
export const useReturnAttributionLazy = () =>
  useLazyQuery(RETURN_ATTRIBUTION, { fetchPolicy: 'network-only' });
/** Creates the default bucket definitions when an environment has none. */
export const useSeedSecurityGroups = () => useMutation(SETUP_DEFAULT_SECURITY_GROUPS);

/** Goal creation is one document per goal kind — see config/goalTypes.js. */
export const useCreateGoal = (document) => useMutation(document);

/**
 * Running the planner on a goal that exists.
 *
 * A subscription, not a query: the backend streams `InvestmentPlanResponse` on
 * `plan<Type>`. The create leaves `investmentplan` null on every goal type, so
 * this is the only way to get the simulation the review step shows and the
 * finalize call needs.
 *
 * `document` is null for a type with no planner (Trading), and `input` is null
 * until the create returns an id — both skip rather than open a stream that
 * cannot resolve a goal. `useSubscription` cannot be called conditionally, so a
 * placeholder document stands in behind the skip.
 */
export function usePlanGoal(document, input) {
  const skip = !document || !input;
  const { data, loading, error } = useSubscription(document ?? PLAN_CAPITAL_TARGET, {
    variables: { input },
    skip,
  });
  const key = Object.keys(data ?? {})[0];
  return {
    supported: Boolean(document),
    skipped: skip,
    /* No message yet is still "running" — the planner takes a few seconds. */
    loading: !skip && (loading || (!data && !error)),
    error,
    plan: key ? data[key] : null,
  };
}

/**
 * Committing the plan a create proposed. One document per goal kind again, and
 * null for the two kinds the backend cannot finalize, so the caller passes what
 * `config/goalTypes.js` gave it and this decides whether there is anything to
 * call. `useMutation` cannot be called conditionally, so a placeholder document
 * stands in and the returned runner refuses instead.
 */
export function useFinalizeGoalPlan(document) {
  const [run, state] = useMutation(document ?? FINALIZE_CAPITAL_TARGET_PLAN);
  const guarded = (options) => {
    if (!document) {
      return Promise.reject(new Error('This goal type has no finalize step on the backend.'));
    }
    return run(options);
  };
  return [guarded, { ...state, supported: Boolean(document) }];
}

/**
 * On-demand scenario pricing. Studio and Proposals used to declare this same
 * lazy query separately; `network-only` because a what-if must never come from
 * cache.
 */
export const useScenarioLazy = () => useLazyQuery(ANALYZE_SCENARIO, { fetchPolicy: 'network-only' });

/**
 * Run the risk engine for one portfolio under one scenario.
 *
 * Not cached: the mutation recomputes and re-persists on every call, so a
 * cached answer would make the button lie about what the engine now holds.
 */
export const useComputePortfolioRisk = () =>
  useMutation(COMPUTE_PORTFOLIO_RISK, { fetchPolicy: 'no-cache' });

/** Compute and store a fund's daily multifactor attribution over a window. */
export const useComputeMfMultifactorAttribution = () =>
  useMutation(COMPUTE_MF_MULTIFACTOR_ATTRIBUTION, { fetchPolicy: 'no-cache' });

/**
 * Running the full compute pipeline against a portfolio — reprice, weights,
 * returns, risk, factors and the rest, whichever stages the caller flags.
 * Never cached: a what-if is recomputed fresh every time it changes.
 */
export const useRecomputePortfolioFull = () =>
  useMutation(RECOMPUTE_PORTFOLIO_FULL, { fetchPolicy: 'no-cache' });

/** Deleting one or more portfolios outright — how a what-if is torn down. */
export const useDeletePortfolios = () =>
  useMutation(DELETE_PORTFOLIOS, { fetchPolicy: 'no-cache' });

/**
 * The alpha engine's three operations.
 *
 * All three queue work and return before any of it is done, so none is cached:
 * a repeat press means "run it again", and an Apollo cache hit would silently
 * make the second press do nothing. What actually happened is read back from
 * the run log or from engine status, never from these results.
 */
export const useRunAlphaBacktest = () =>
  useMutation(RUN_ALPHA_BACKTEST, { fetchPolicy: 'no-cache' });
export const useCalibrateAlphaWeights = () =>
  useMutation(CALIBRATE_ALPHA_WEIGHTS, { fetchPolicy: 'no-cache' });
export const useRegenerateAlphaSignals = () =>
  useMutation(REGENERATE_ALPHA_SIGNALS, { fetchPolicy: 'no-cache' });

/**
 * Asking the review agents to write a subject again.
 *
 * The agents run nightly; these exist for a subject the night run has not
 * covered or whose inputs have moved since. Every one *enqueues* — `success`
 * means the job was accepted, not that a review is ready — so the caller
 * refetches the read rather than looking for an artifact in the response, and
 * none is cached: a second press means "run it again".
 */
export const useRunFundReview = () =>
  useMutation(RUN_MF_PM_REVIEW, { fetchPolicy: 'no-cache' });
export const useRunManagerReview = () =>
  useMutation(RUN_FUND_MANAGER_REVIEW, { fetchPolicy: 'no-cache' });
export const useRunBookReview = () =>
  useMutation(RUN_ADVISOR_BOOK_REVIEW, { fetchPolicy: 'no-cache' });
/* The same contract for the other two subjects a review can be about. */
export const useRunModelReview = () =>
  useMutation(RUN_MODEL_REVIEW, { fetchPolicy: 'no-cache' });
export const useRunPortfolioReview = () =>
  useMutation(RENDER_PORTFOLIO_REVIEW, { fetchPolicy: 'no-cache' });
export const useRunSchemeReview = () =>
  useMutation(RUN_MUTUAL_FUND_REVIEW, { fetchPolicy: 'no-cache' });

/**
 * Analyse a portfolio in the background: risk, attribution, exposures and goal
 * progress, then its review and the client's. Queued (`queue: true`), so it
 * returns at once and the background-jobs tracker follows it — the reader can
 * leave the screen and a toast says when it is done.
 *
 * `analyze(symbol, { clientUserId, name })` resolves to the call's
 * `{ success, queued, portfolio_symbol }`; `isAnalyzing(symbol)` is true from
 * the click until the analysis finishes, for disabling the button.
 */
export function useAnalyzePortfolio() {
  const [run] = useMutation(RUN_PORTFOLIO_ANALYTICS, { fetchPolicy: 'no-cache' });
  const { start, isActive } = useBackgroundJobs();
  const analyze = useCallback((securitySymbol, meta = {}) => start(
    run({ variables: { securitySymbol, queue: true } })
      .then(({ data }) => data?.runPortfolioAnalytics ?? null),
    { subject: securitySymbol, type: ANALYSIS_KIND, ...meta },
  ), [run, start]);
  const isAnalyzing = useCallback((symbol) => isActive(symbol, ANALYSIS_KIND), [isActive]);
  return { analyze, isAnalyzing };
}

const RESEARCH_POLL_MS = 30000;
const RESEARCH_POLL_LIMIT_MS = 20 * 60 * 1000;
/* The research runs queued from this session, by scheme: the edition that was
   current when each was asked for, and when. Outside the component so leaving
   a fund and coming back finds its run still in hand rather than offering to
   pay for a second. */
const researchRuns = new Map();

/**
 * Write one scheme's research report now, and follow the run until it lands.
 *
 * `triggerMfResearchBatchAgent` only queues: the four-P research is written by the review
 * agents and renders its own report when it ends, minutes later. So the run is
 * followed by re-reading the scheme's report list (`refetch`) until the
 * edition on screen (`edition`, any stable identity of the current report, or
 * null for none) is no longer the one the run started from.
 *
 *   generate({ window, mode })  queue a run — quarterly / monthly, batch /
 *                 sync, quarterly batch when not given. Resolves to the call's `{ success, message }`,
 *                 or null when there was nothing to do (no scheme, or a run
 *                 already in hand). Throws on a transport error.
 *   generating    true from the click until the run settles — disable on it.
 *   onSettled(outcome)  'done' once a new edition has been read, 'timeout'
 *                 after twenty minutes of waiting.
 *
 * `loading` holds the comparison while the list itself is still being read,
 * so a fund reopened mid-run is not taken for one with a new report.
 */
export function useGenerateFundResearch(securitySymbol, { edition = null, loading = false, refetch, onSettled } = {}) {
  const [run] = useMutation(TRIGGER_MF_RESEARCH_BATCH_AGENT, { fetchPolicy: 'no-cache' });
  const [, redraw] = useState(0);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  const reread = useRef(refetch);
  reread.current = refetch;
  const inHand = (securitySymbol && researchRuns.get(securitySymbol)) || null;

  const drop = useCallback((symbol) => {
    researchRuns.delete(symbol);
    redraw((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!inHand || loading || edition === inHand.before) return;
    drop(securitySymbol);
    settled.current?.('done');
  }, [inHand, loading, edition, securitySymbol, drop]);

  useEffect(() => {
    if (!inHand) return undefined;
    const timer = setInterval(() => {
      if (Date.now() - inHand.since > RESEARCH_POLL_LIMIT_MS) {
        drop(securitySymbol);
        settled.current?.('timeout');
      } else {
        reread.current?.();
      }
    }, RESEARCH_POLL_MS);
    return () => clearInterval(timer);
  }, [inHand, securitySymbol, drop]);

  /* `window`: "quarterly" writes a full new note, "monthly" updates the
     latest quarterly one. `mode`: "batch" (cheaper, slower) or "sync" (live). */
  const generate = useCallback(async ({ window: runWindow = 'quarterly', mode = 'batch' } = {}) => {
    if (!securitySymbol || researchRuns.has(securitySymbol)) return null;
    /* Marked before the call, so two clicks in one tick cannot both queue. */
    researchRuns.set(securitySymbol, { before: edition, since: Date.now() });
    redraw((n) => n + 1);
    try {
      const { data } = await run({
        variables: { securitySymbols: [securitySymbol], window: runWindow, mode },
      });
      const res = data?.triggerMfResearchBatchAgent ?? null;
      if (!res?.success) drop(securitySymbol);
      return res;
    } catch (e) {
      drop(securitySymbol);
      throw e;
    }
  }, [run, securitySymbol, edition, drop]);

  return { generate, generating: Boolean(inHand) };
}

/**
 * Queue research for many schemes in one call — the screener's "Run research
 * for selected". Same mutation as `useGenerateFundResearch`, but fire-and-
 * forget: each scheme's report lands on its own page when its run ends.
 */
export const useTriggerMfResearchBatch = () => useMutation(TRIGGER_MF_RESEARCH_BATCH_AGENT, { fetchPolicy: 'no-cache' });

const REVIEW_POLL_MS = 5000;
const REVIEW_POLL_LIMIT_MS = 5 * 60 * 1000;
/* A run's identity across looks, and the last finished run each portfolio's
   review was re-read for — so a screen opened again does not re-read for a run
   it has already shown. Session-long; a reload simply re-reads once more. */
const reviewRunId = (row) => `${row.key}:${row.enqueuedAt ?? ''}`;
const reviewReadFor = new Map();

/**
 * Write one portfolio's review again, on its own, and follow the run to the end.
 *
 * Unlike `useAnalyzePortfolio` this does not go through the background-jobs
 * tracker. The review is requested on the CLIENT's behalf (`X-Target-User-Id`),
 * and its run is followed the same way — asking for this one portfolio's
 * events as the client — because the tracker reads the signed-in user's list.
 * So this hook keeps its own short poll, and only while a run is in hand.
 *
 *   refresh()   queue a run at `window` and follow it. Resolves to the call's
 *               `{ success, message }`, or null when there was nothing to do
 *               (no portfolio, or a request already in hand). A run found
 *               already queued or running is followed instead of paid for twice.
 *   refreshing  true from the click until the run settles — disable on it.
 *   onSettled(outcome)  'done' once the new review has been re-read,
 *               'failed', or 'timeout' after five minutes of waiting. A
 *               cancelled run settles without a call.
 *
 * On mount it looks once: a run still queued or running is picked up, so
 * leaving and coming back cannot queue a second; a run that finished while
 * nobody was looking has its review re-read, since nothing else would.
 * Nothing on screen is cleared at any point — the reads refetch in place.
 */
export function useRefreshPortfolioReview(portfolioSymbol, clientUserId, { window = 'daily', onSettled } = {}) {
  const client = useApolloClient();
  const [run] = useMutation(RUN_PORTFOLIO_REVIEW, { fetchPolicy: 'no-cache' });
  const [refreshing, setRefreshing] = useState(false);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  /* One request or run in hand at a time — a ref, because two clicks in one
     tick both read the same state. */
  const busy = useRef(false);
  const timer = useRef(null);
  /* Bumped when the portfolio changes or the screen goes: anything still in
     flight from before finds it moved and lets go. */
  const epoch = useRef(0);

  const readRun = useCallback(async () => {
    const { data } = await client.query({
      query: TRIGGERED_EVENTS,
      variables: { subject: portfolioSymbol },
      context: asClient(clientUserId),
      fetchPolicy: 'no-cache',
    });
    return newestReviewRun((data?.getTriggeredEvents ?? []).filter((r) => !r.subject || r.subject === portfolioSymbol));
  }, [client, portfolioSymbol, clientUserId]);

  /* Re-ask the review reads that are on screen — the same list the tracker
     refetches for a finished review run (config/jobTypes.js). */
  const rereadReview = useCallback(async (row) => {
    const onScreen = new Set([...client.getObservableQueries('active').values()].map((q) => q.queryName));
    const include = JOB_TYPES[REVIEW_RUN].queriesToRefetch.filter((name) => onScreen.has(name));
    if (include.length) await client.refetchQueries({ include });
    reviewReadFor.set(portfolioSymbol, reviewRunId(row));
  }, [client, portfolioSymbol]);

  const follow = useCallback((baseline, alreadyActive) => {
    const mine = epoch.current;
    const deadline = Date.now() + REVIEW_POLL_LIMIT_MS;
    let seenActive = alreadyActive;
    const finish = (outcome) => {
      if (epoch.current !== mine) return;
      busy.current = false;
      setRefreshing(false);
      if (outcome) settled.current?.(outcome);
    };
    const look = async () => {
      if (epoch.current !== mine) return;
      let row = null;
      let step = 'wait';
      try {
        row = await readRun();
        ({ step, seenActive } = reviewRunStep(row, { baseline, seenActive }));
      } catch {
        /* a look that failed saw nothing; the next one asks again */
      }
      if (epoch.current !== mine) return;
      if (step === 'done') {
        /* The toast still goes if the re-read fails: the review is there,
           only this screen's copy is old. */
        await rereadReview(row).catch(() => {});
        finish('done');
      } else if (step === 'failed') finish('failed');
      else if (step === 'stopped') finish(null);
      else if (Date.now() >= deadline) finish('timeout');
      else timer.current = setTimeout(look, REVIEW_POLL_MS);
    };
    busy.current = true;
    setRefreshing(true);
    look();
  }, [readRun, rereadReview]);

  useEffect(() => {
    if (!portfolioSymbol) return undefined;
    const mine = epoch.current;
    readRun().then((row) => {
      if (epoch.current !== mine || busy.current || !row) return;
      if (isReviewRunActive(row)) follow(undefined, true);
      else if (row.state === 'DONE' && reviewReadFor.get(portfolioSymbol) !== reviewRunId(row)) {
        rereadReview(row).catch(() => {});
      }
    }).catch(() => {});
    return () => {
      epoch.current += 1;
      clearTimeout(timer.current);
      busy.current = false;
      setRefreshing(false);
    };
  }, [portfolioSymbol, readRun, rereadReview, follow]);

  const refresh = useCallback(async () => {
    if (!portfolioSymbol || busy.current) return null;
    const mine = epoch.current;
    const letGo = () => {
      if (epoch.current !== mine) return;
      busy.current = false;
      setRefreshing(false);
    };
    busy.current = true;
    setRefreshing(true);
    try {
      /* The newest run as it stands before asking — what tells the new run
         from an old one afterwards. Unreadable is not fatal: see reviewRunStep. */
      const baseline = await readRun().catch(() => undefined);
      if (epoch.current !== mine) return null;
      if (isReviewRunActive(baseline)) {
        follow(baseline, true);
        return { success: true, message: null };
      }
      const { data } = await run({
        variables: { portfolioSecuritySymbol: portfolioSymbol, window },
        context: asClient(clientUserId),
      });
      const result = data?.runPortfolioReview ?? { success: false, message: null };
      if (epoch.current !== mine) return result;
      if (result.success) follow(baseline, false);
      else letGo();
      return result;
    } catch (e) {
      letGo();
      throw e;
    }
  }, [portfolioSymbol, clientUserId, window, run, readRun, follow]);

  return { refresh, refreshing };
}

/**
 * The analytics a tab can ask for when nothing is stored.
 *
 * These COMPUTE in the request rather than enqueueing, so they take longer to
 * answer and the result is real when it returns — but the screens still refetch
 * the stored read afterwards, because that is what every other panel on the tab
 * is drawing from.
 */
export const useComputeExposures = () =>
  useMutation(COMPUTE_PORTFOLIO_EXPOSURES, { fetchPolicy: 'no-cache' });
export const useComputeMultifactor = () =>
  useMutation(COMPUTE_MULTIFACTOR_ATTRIBUTION, { fetchPolicy: 'no-cache' });

/** Stock research reports — generate, mark reviewed, and send a correction back. */
/** The stock research agent for one or many stocks — "Generate report" on the stock pages. */
export const useTriggerStockResearchAgent = () =>
  useMutation(TRIGGER_STOCK_RESEARCH_AGENT, { fetchPolicy: 'no-cache' });
export const useVerifyStockReport = () => useMutation(VERIFY_STOCK_REPORT);

/**
 * Backtesting a change to a composition. Never cached: the same trades against
 * the same portfolio are asked again precisely because something was edited.
 */
export const useBacktestPortfolioTrades = () =>
  useMutation(BACKTEST_PORTFOLIO_TRADES, { fetchPolicy: 'no-cache' });

/* ── passwords ──────────────────────────────────────────────────────────── */

/** Start a reset: an emailed link, or an OTP by SMS. */
export const useSendForgotPasswordOtp = () => useMutation(SEND_FORGOT_PASSWORD_OTP);
/** SMS path only — exchanges the OTP for the reset token. */
export const useVerifyForgotPasswordOtp = () => useMutation(VERIFY_FORGOT_PASSWORD_OTP);
/** Both paths end here: a reset token and the new password. */
export const useResetPassword = () => useMutation(RESET_PASSWORD);
/** Changing a known password from inside a session. */
export const useUpdatePassword = () => useMutation(UPDATE_PASSWORD);

/* Stock alpha desk writes. Each refetches the list it changes so the screen
   moves with it; each is role-gated on the server (see mutations/stockAlpha). */
export const useReviewAlphaThesis = () => useMutation(REVIEW_ALPHA_THESIS, { refetchQueries: ['StockAlphaTheses', 'StockAlphaThesis'] });
export const usePublishAlphaThesis = () => useMutation(PUBLISH_ALPHA_THESIS, { refetchQueries: ['StockAlphaTheses', 'StockAlphaThesis'] });
export const useAcknowledgeAlphaAlert = () => useMutation(ACKNOWLEDGE_ALPHA_ALERT, { refetchQueries: ['StockAlphaAlerts'] });
export const useResolveAlphaAlert = () => useMutation(RESOLVE_ALPHA_ALERT, { refetchQueries: ['StockAlphaAlerts'] });
export const useHaltAlphaPaperBook = () => useMutation(HALT_ALPHA_PAPER_BOOK, { refetchQueries: ['StockAlphaPaperBooks'] });
export const useResumeAlphaPaperBook = () => useMutation(RESUME_ALPHA_PAPER_BOOK, { refetchQueries: ['StockAlphaPaperBooks'] });
/* Starts the company-report job on the server; the report lands in
   alphaCompanyReports minutes later, which the caller polls. */
export const useGenerateCompanyReport = () => useMutation(GENERATE_COMPANY_REPORT);
/* Starts the day's scoring (`alpha.daily`) for a date; the ranking moves to
   that date minutes later, which the caller polls. */
export const useRunAlphaScoring = () => useMutation(RUN_ALPHA_SCORING, { fetchPolicy: 'no-cache' });

/* The company reports started from this session, by stock — kept outside the
   component for the same reason as `researchRuns`: leaving a stock and coming
   back finds its run still in hand rather than offering a second. */
const companyReportRuns = new Map();

/**
 * Write one stock's research report now, and follow the run until it lands —
 * `useGenerateFundResearch` for a stock. `generateCompanyReport` only starts a
 * background job, so the run is followed by re-reading the stock's report list
 * (`refetch`) until the latest report (`edition`, its id, or null for none) is
 * no longer the one the run started from.
 *
 *   generate()    start a run. Resolves to `{ success, message }`, or null when
 *                 there was nothing to do. Throws on a transport error.
 *   generating    true from the click until the run settles.
 *   onSettled(outcome)  'done' once a new report has been read, 'timeout'
 *                 after twenty minutes of waiting.
 */
export function useGenerateStockReport(securitySymbol, { edition = null, loading = false, refetch, onSettled } = {}) {
  const [run] = useMutation(GENERATE_COMPANY_REPORT, { fetchPolicy: 'no-cache' });
  const [, redraw] = useState(0);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  const reread = useRef(refetch);
  reread.current = refetch;
  const inHand = (securitySymbol && companyReportRuns.get(securitySymbol)) || null;

  const drop = useCallback((symbol) => {
    companyReportRuns.delete(symbol);
    redraw((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!inHand || loading || edition === inHand.before) return;
    drop(securitySymbol);
    settled.current?.('done');
  }, [inHand, loading, edition, securitySymbol, drop]);

  useEffect(() => {
    if (!inHand) return undefined;
    const timer = setInterval(() => {
      if (Date.now() - inHand.since > RESEARCH_POLL_LIMIT_MS) {
        drop(securitySymbol);
        settled.current?.('timeout');
      } else {
        reread.current?.();
      }
    }, RESEARCH_POLL_MS);
    return () => clearInterval(timer);
  }, [inHand, securitySymbol, drop]);

  const generate = useCallback(async () => {
    if (!securitySymbol || companyReportRuns.has(securitySymbol)) return null;
    companyReportRuns.set(securitySymbol, { before: edition, since: Date.now() });
    redraw((n) => n + 1);
    try {
      const { data } = await run({ variables: { securitySymbol } });
      const res = data?.generateCompanyReport ?? null;
      if (!res?.started) drop(securitySymbol);
      return res && { success: Boolean(res.started), message: res.message };
    } catch (e) {
      drop(securitySymbol);
      throw e;
    }
  }, [run, securitySymbol, edition, drop]);

  return { generate, generating: Boolean(inHand) };
}
